import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

export default function NextHealthProposalTemplate() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl font-serif font-medium text-indigo-950 mb-4">
              Consulting Proposal: Strategic Partnership for Next Health
            </h1>
            <div className="text-gray-700 space-y-1 mb-6">
              <p><strong>Prepared for: Dr. Darshan Shah</strong></p>
              <p><strong>Prepared by: Anant Vinjamoori, MD, MBA</strong></p>
              <p><strong>Email:</strong> <a href="mailto:anant@nextgenerationmedicine.co" className="text-blue-600 hover:text-blue-800">anant@nextgenerationmedicine.co</a></p>
              <p><strong>Website:</strong> <a href="https://www.nextgenerationmedicine.co" className="text-blue-600 hover:text-blue-800">nextgenerationmedicine.co</a></p>
            </div>
          </div>

          <hr className="border-gray-400 mb-12" />

          {/* Executive Summary */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-4">
              Executive Summary
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Next Health is building one of the nation's most ambitious platforms for delivering longevity medicine in a physical setting. With rapid expansion, a strong internal technology team, and a clear operational strategy, the opportunity is to solidify your leadership with systems that match your scale. The next phase is about enabling your clinicians and franchisees with tools that are efficient, consistent, and aligned with your brand vision. The aim: to automate and standardize critical workflows—particularly report generation, diagnostics, and business intelligence—so your team can focus on delivering exceptional patient care.
              </p>
              <p>
                Based on our discussion, the <strong>Strategic Partnership</strong> engagement will best support your objectives. This approach will embed robust technology and clinical strategy into the core of Next Health's offering, positioning your team to deliver excellence as you scale.
              </p>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Engagement Options */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-6">
              Engagement Options
            </h2>

            {/* Comparison Table */}
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse border border-gray-400">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Tier</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Scope</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Cadence</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Investment</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">What Success Looks Like</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-4 py-3 font-bold">Operating Advisor</td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Clinical and business strategy support. Access to full protocol and template library.
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Up to 4 hours/month live calls + async Slack/email
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-lg font-bold">
                      $75,000/year
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Strategic clarity, improved conversion, and scalable clinician training.
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-3 font-bold">Strategic Partnership<br/><em>(Recommended)</em></td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Operating Advisor scope <strong>plus</strong> tailored software support: AI-powered report generation, dashboarding, and diagnostics tools.
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Up to 4 hours/month live calls<br/>1 innovation sprint per year
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-lg font-bold">
                      $150,000/year
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Next Health operates with a seamless digital backbone, accelerating patient engagement, reporting, and operational efficiency across all locations.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Strategic Partnership Deliverables */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-6">
              Strategic Partnership: Deliverables
            </h2>
            <p className="text-gray-700 mb-6">
              This tier is designed for organizations focused on long-term growth, sophisticated operations, and industry leadership. The partnership includes:
            </p>

            {/* Core Support */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-indigo-950 mb-4">
                Core Support
              </h3>
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li>Quarterly strategic roadmap and KPI review</li>
                <li>Access to the complete clinical and operational protocol library (slide decks, training SOPs, checklists)</li>
                <li>On-demand support via Slack/email (48h response)</li>
              </ul>
            </div>

            {/* Custom Tooling */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-indigo-950 mb-4">
                Custom Tooling and API Integration
              </h3>
              <p className="text-gray-700 mb-4">Development and ongoing support for:</p>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Automated Diagnostic Report Generator</h4>
                  <ul className="list-disc ml-6 space-y-1 text-blue-800 text-sm">
                    <li><strong>Input:</strong> PDF labs and clinician notes</li>
                    <li><strong>Output:</strong> Structured, patient-friendly reports tailored to your clinical model and visual framework</li>
                    <li><strong>Benefit:</strong> Streamlined, high-quality patient reporting to support conversion and operational consistency</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">Business Intelligence Dashboards</h4>
                  <ul className="list-disc ml-6 space-y-1 text-green-800 text-sm">
                    <li><strong>Input:</strong> Visit transcripts (e.g., Nabla) or structured practice data</li>
                    <li><strong>Output:</strong> Actionable metrics on clinic performance (conversion rates, visit types, engagement patterns)</li>
                    <li><strong>Benefit:</strong> Practical, accessible insights for franchisees and medical directors to drive outcomes</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">Seamless Integration with Your Internal Design</h4>
                  <ul className="list-disc ml-6 space-y-1 text-purple-800 text-sm">
                    <li>Delivery of core data in flexible formats, supporting easy adaptation into Next Health's proprietary presentation and branding</li>
                    <li><strong>Benefit:</strong> Maintains brand standards while eliminating manual data synthesis</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Annual Innovation Sprint */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-indigo-950 mb-4">
                Annual Innovation Sprint
              </h3>
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li>A collaborative session (virtual or onsite) with your executive and product teams</li>
                <li>Focus: Co-design new solutions, review implementation, and identify future needs</li>
              </ul>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Quick Wins */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-4">
              Quick Wins (First 60 Days)
            </h2>
            <ul className="list-disc ml-6 space-y-2 text-gray-700">
              <li>Deploy automated report generation at 1–2 pilot sites</li>
              <li>Integrate core Next Health branding and frameworks into report templates</li>
              <li>Launch prototype BI dashboard with priority metrics for operational insight</li>
              <li>Begin planning for longitudinal reporting to support patient retention and outcome tracking</li>
            </ul>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Why Work With Me */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-4">
              Why Work With Me
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                I bring a rare blend of clinical, operational, and technology expertise to longevity medicine. As a Harvard-trained physician and executive, I've led and scaled national clinics like Modern Age, served as Director of Product at Virta Health through its growth to a $3B+ valuation, and advised major health systems and startups on both business and clinical strategy.
              </p>
              <p>
                My experience is practical and results-driven. I've developed and deployed real-world solutions—from AI-powered reporting tools to actionable clinic dashboards—always focused on what works at scale, not just in theory. My work has been featured in leading publications and conferences, but what matters most is delivering operational wins for my partners.
              </p>
              <p>
                If you want a partner who understands how to build, scale, and optimize the next generation of longevity clinics, I'm uniquely positioned to help you achieve it—with strategy, technology, and execution that deliver real results.
              </p>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Next Steps */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-4">
              Next Steps
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                The <strong>Strategic Partnership</strong> tier aligns with your goals and readiness to implement solutions at scale.
                I recommend scheduling a session with your CTO, President, and CEO to review this proposal, finalize priorities, and initiate NDA + SOW.
              </p>
              <p>
                Thank you for the opportunity to collaborate.
              </p>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Contact Information */}
          <section className="text-left">
            <div className="text-gray-700">
              <p><strong>Anant Vinjamoori</strong></p>
              <p>President, Next Generation Medicine</p>
              <p><a href="mailto:anant@nextgenerationmedicine.co" className="text-blue-600 hover:text-blue-800">anant@nextgenerationmedicine.co</a></p>
              <p><a href="https://www.nextgenerationmedicine.co" className="text-blue-600 hover:text-blue-800">www.nextgenerationmedicine.co</a></p>
            </div>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}